<template>
  <div class="min-h-screen bg-white dark:bg-gradient-to-br dark:from-[#1a1a1a] dark:to-[#0c0c0c] p-4 md:p-6">
    <div class="max-w-7xl mx-auto space-y-8">

      <!-- Deals Management Section -->
      <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-xl border border-[#E5E7EB] dark:border-[#374151] overflow-hidden">
        <!-- Header Section -->
        <div class="bg-gradient-to-r from-[#007AFF] to-[#5856D6] dark:from-[#0056CC] dark:to-[#4338CA] p-6">
          <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <!-- Title and Action Buttons -->
            <div class="flex items-center gap-4">
              <div class="text-left">
                <h1 class="text-2xl font-bold text-white">{{ directT('crm.deals.dealsManagement') }}</h1>
                <p class="text-white">{{ directT('crm.deals.manageDealsSubtitle') }}</p>
              </div>
            </div>
            
            <div class="flex items-center gap-4">
              <router-link
                to="/crm/deals/create"
                class="inline-flex items-center px-6 py-3 rounded-2xl bg-white/20 dark:bg-black/20 backdrop-blur-sm border border-white/30 dark:border-white/20 text-white font-medium hover:bg-white/30 dark:hover:bg-black/30 transition-all duration-200 shadow-lg"
              >
                <i class="ri-add-line text-xl mr-2"></i>
                {{ directT('crm.deals.addNewDeal') }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- Content Section -->
        <div class="p-6">
          <!-- Search Section -->
          <div class="mb-6">
            <div class="relative max-w-md">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i class="ri-search-line text-[#666666] dark:text-[#cccccc] text-xl"></i>
              </div>
              <input
                type="text"
                v-model="searchTerm"
                :placeholder="directT('crm.deals.searchDeal')"
                class="w-full h-12 pl-12 pr-4 rounded-2xl bg-white dark:bg-[#2a2a2a] border border-[#E5E7EB] dark:border-[#374151] text-[#1a1a1a] dark:text-white placeholder-[#666666] dark:placeholder-[#cccccc] focus:outline-none focus:ring-2 focus:ring-[#007AFF]/50 focus:border-[#007AFF] transition-all duration-200"
              />
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex justify-center items-center py-16">
            <div class="relative">
              <div class="w-16 h-16 border-4 border-[#007AFF]/30 border-t-[#007AFF] rounded-full animate-spin"></div>
              <div class="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-[#34C759] rounded-full animate-spin" style="animation-delay: -0.3s;"></div>
            </div>
          </div>
          
          <!-- No Deals Found -->
          <div v-else-if="deals.length === 0" class="flex flex-col justify-center items-center py-16">
            <div class="w-24 h-24 bg-gradient-to-br from-[#007AFF] to-[#5856D6] rounded-3xl flex items-center justify-center mb-6">
              <i class="ri-handshake-line text-white !text-[48px]"></i>
            </div>
            <p class="text-xl font-semibold text-[#1a1a1a] dark:text-white mb-4">{{ directT('crm.deals.noDealsFound') }}</p>
            

          </div>
          
          <!-- Deals Table -->
          <div v-else class="bg-white dark:bg-[#1a1a1a] rounded-2xl overflow-hidden border border-[#E5E7EB] dark:border-[#374151]">
            <div class="overflow-x-auto">
              <table class="w-full">
                <thead>
                  <tr class="bg-gradient-to-r from-[#007AFF] to-[#5856D6] dark:from-[#0056CC] dark:to-[#4338CA]">
                    <th class="px-6 py-4 text-left text-white font-semibold">
                      {{ directT('crm.deals.id') }}
                    </th>
                    <th class="px-6 py-4 text-center text-white font-semibold">
                      {{ directT('crm.deals.title') }}
                    </th>
                    <th class="px-6 py-4 text-left text-white font-semibold">
                      {{ directT('crm.deals.contactPerson') }}
                    </th>
                    <th class="px-6 py-4 text-left text-white font-semibold">
                      {{ directT('crm.deals.amount') }}
                    </th>
                    <th class="px-6 py-4 text-left text-white font-semibold">
                      {{ directT('crm.deals.createdAt') }}
                    </th>
                    <th class="px-6 py-4 text-left text-white font-semibold">
                      {{ directT('crm.deals.status') }}
                    </th>
                    <th class="px-6 py-4 text-left text-white font-semibold">
                      {{ directT('crm.deals.action') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="deal in filteredDeals" :key="deal.id"
                      class="bg-white dark:bg-[#1a1a1a] border-b border-[#E5E7EB] dark:border-[#374151] hover:bg-[#F0F7FF] dark:hover:bg-[#2a2a2a] transition-all duration-200">
                    <td class="px-6 py-4">
                      <div class="flex items-center">
                        <div class="w-10 h-10 bg-gradient-to-br from-[#007AFF] to-[#5856D6] rounded-2xl flex items-center justify-center mr-3">
                          <i class="ri-file-text-line text-white text-lg"></i>
                        </div>
                        <span class="text-sm font-medium text-[#666666] dark:text-[#cccccc]">
                          {{ shortId(deal.id) }}
                        </span>
                      </div>
                    </td>
                    <td class="px-6 py-4 text-center">
                      <div class="font-semibold text-[#1a1a1a] dark:text-white">{{ deal.title }}</div>
                      <div v-if="deal.description" class="text-sm text-[#666666] dark:text-[#cccccc] truncate max-w-xs">
                        {{ deal.description }}
                      </div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="font-medium text-[#1a1a1a] dark:text-white">
                        {{ deal.first_name && deal.last_name ? `${deal.first_name} ${deal.last_name}` : (deal.name || directT('crm.deals.notDefined')) }}
                      </div>
                      <div v-if="deal.phone" class="text-sm text-[#666666] dark:text-[#cccccc]">
                        {{ deal.phone }}
                      </div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="font-bold text-lg text-[#34C759]">
                        {{ formatCurrency(deal.amount) }}
                      </div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="text-[#1a1a1a] dark:text-white font-medium">
                        {{ formatDate(deal.created_at) }}
                      </div>
                    </td>
                    <td class="px-6 py-4">
                      <span 
                        class="px-3 py-1 rounded-xl font-medium text-sm border"
                        :class="getStatusClass(deal.status)"
                      >
                        {{ getStatusText(deal.status) }}
                      </span>
                    </td>
                    <td class="px-6 py-4">
                      <div class="flex items-center gap-2">
                        <button
                          type="button"
                          class="p-2 rounded-xl bg-[#007AFF]/10 text-[#007AFF] hover:bg-[#007AFF]/20 transition-all duration-200"
                          :title="directT('crm.deals.view')"
                          @click="viewDeal(deal.id)"
                        >
                          <i class="ri-eye-line text-lg"></i>
                        </button>
                        <button
                          type="button"
                          class="p-2 rounded-xl bg-[#34C759]/10 text-[#34C759] hover:bg-[#34C759]/20 transition-all duration-200"
                          :title="directT('crm.deals.edit')"
                          @click="editDeal(deal.id)"
                        >
                          <i class="ri-edit-line text-lg"></i>
                        </button>
                        <button
                          type="button"
                          class="p-2 rounded-xl bg-[#FF3B30]/10 text-[#FF3B30] hover:bg-[#FF3B30]/20 transition-all duration-200"
                          :title="directT('crm.deals.delete')"
                          @click="confirmDeleteDeal(deal.id)"
                        >
                          <i class="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="border-t border-[#E5E7EB] dark:border-[#374151] bg-white dark:bg-[#0c0c0c]">
              <Pagination
                :items="10"
                :total="36"
                class="px-6 py-4"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Delete Confirmation Popup -->
    <div v-if="showDeletePopup" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-2xl border border-[#E5E7EB] dark:border-[#374151] max-w-md w-full mx-4 overflow-hidden">
        <!-- Header -->
        <div class="bg-gradient-to-r from-[#FF3B30] to-[#FF2D92] p-6">
          <div class="flex items-center gap-4">
            <div class="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <i class="ri-delete-bin-line text-white text-xl"></i>
            </div>
            <div>
              <h3 class="text-xl font-bold text-white">{{ directT('crm.deals.deleteConfirmTitle') }}</h3>
              <p class="text-white/80 text-sm">{{ directT('crm.deals.deleteConfirmSubtitle') }}</p>
            </div>
          </div>
        </div>
        
        <!-- Content -->
        <div class="p-6">
          <p class="text-[#1a1a1a] dark:text-white text-center mb-6">
            {{ directT('crm.deals.deleteConfirmMessage') }}
          </p>
          
          <!-- Actions -->
          <div class="flex gap-3">
            <button 
              @click="cancelDelete"
              class="flex-1 px-4 py-3 bg-[#F3F4F6] dark:bg-[#374151] text-[#1a1a1a] dark:text-white font-medium rounded-2xl transition-all duration-200 hover:bg-[#E5E7EB] dark:hover:bg-[#4B5563]"
            >
              {{ directT('crm.deals.cancel') }}
            </button>
            <button 
              @click="confirmDelete"
              class="flex-1 px-4 py-3 bg-gradient-to-r from-[#FF3B30] to-[#FF2D92] text-white font-medium rounded-2xl transition-all duration-200 hover:from-[#E6342A] hover:to-[#E6297D] shadow-lg"
            >
              {{ directT('crm.deals.delete') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, onBeforeUnmount } from 'vue';
import { supabase } from '@/lib/supabaseClient';
import { createToast } from '@/utils/toast';
import emitter from '@/utils/emitter';
import Pagination from "@/components/Common/Pagination.vue";
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

type TranslationObject = Record<string, any>;

interface Deal {
  id: string;
  organization_id: string;
  title: string;
  description: string | null;
  status: string;
  amount: number | null;
  closedby: string | null;
  connecttoclients: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  name: string | null;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface Client {
  id: string;
  first_name?: string;
  last_name?: string;
}

export default defineComponent({
  name: 'DealsTable',
  components: {
    Pagination
  },
  setup() {
    const router = useRouter();
    const { locale } = useI18n();
    const deals = ref<Deal[]>([]);
    const loading = ref(true);
    const searchTerm = ref('');
    const organizationCurrency = ref('USD'); // Default currency code
    const currencySymbol = ref('$'); // Default currency symbol
    
    // Delete confirmation popup
    const showDeletePopup = ref(false);
    const dealToDelete = ref<string | null>(null);
    
    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    
    // Formatting functions
    const formatCurrency = (value: number | null): string => {
      if (value === null) return `0.00 ${currencySymbol.value}`;
      
      try {
        // Try to use the standard formatter with currency code
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: organizationCurrency.value,
          minimumFractionDigits: 2
        }).format(value);
      } catch (error) {
        // Fallback if there's an error - use the symbol directly
        console.warn('Error formatting currency:', error);
        return `${value.toFixed(2)} ${currencySymbol.value}`;
      }
    };
    
    const formatDate = (dateString: string | null): string => {
      if (!dateString) return 'N/A';
      
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };
    
    const shortId = (id: string): string => {
      // Shorten UUID for display
      return id ? '#' + id.split('-')[0] : '';
    };
    
    const getStatusText = (status: string): string => {
      const statusMap: Record<string, string> = {
        'new': directT('crm.deals.statuses.new'),
        'in progress': directT('crm.deals.statuses.inProgress'),
        'negotiation': directT('crm.deals.statuses.negotiation'),
        'closed won': directT('crm.deals.statuses.closedWon'),
        'closed lost': directT('crm.deals.statuses.closedLost')
      };
      
      return statusMap[status.toLowerCase()] || status;
    };
    
    // Get status class for styling - Updated for modern design
    const getStatusClass = (status: string): string => {
      switch (status?.toLowerCase()) {
        case 'new':
          return 'bg-[#F0F7FF] dark:bg-[#0c1427] text-[#007AFF] border border-[#007AFF]/20';
        case 'in progress':
          return 'bg-[#F0F9F5] dark:bg-[#0a1a0f] text-[#34C759] border border-[#34C759]/20';
        case 'negotiation':
          return 'bg-[#FFF7E6] dark:bg-[#1a1406] text-[#FF9500] border border-[#FF9500]/20';
        case 'closed won':
          return 'bg-[#F0F9F5] dark:bg-[#0a1a0f] text-[#34C759] border border-[#34C759]/20';
        case 'closed lost':
          return 'bg-[#FEF2F2] dark:bg-[#1a0f0f] text-[#FF3B30] border border-[#FF3B30]/20';
        default:
          return 'bg-[#F3F4F6] dark:bg-[#374151] text-[#6B7280] border border-[#6B7280]/20';
      }
    };
    
    // Filter deals by search term
    const filteredDeals = computed(() => {
      if (!searchTerm.value.trim()) {
        return deals.value;
      }
      
      const term = searchTerm.value.toLowerCase();
      return deals.value.filter(deal => 
        deal.title?.toLowerCase().includes(term) || 
        deal.description?.toLowerCase().includes(term) ||
        deal.name?.toLowerCase().includes(term) ||
        (deal.first_name && deal.first_name.toLowerCase().includes(term)) ||
        (deal.last_name && deal.last_name.toLowerCase().includes(term))
      );
    });
    
    // Load all deals
    const fetchDeals = async () => {
      try {
        loading.value = true;
        console.log('Starting to fetch deals...');
        
        // Get current user organization ID
        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error('Error getting user:', userError);
          throw userError;
        }
        
        console.log('User data:', userData);
        
        if (!userData.user) {
          console.error('No user found in auth data');
          createToast('User is not authenticated', 'error');
          loading.value = false;
          return;
        }
        
        console.log('User ID:', userData.user.id);
        
        // Get user profile with organization ID
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', userData.user.id)
          .single();
          
        if (profileError) {
          console.error('Error getting user profile:', profileError);
          throw profileError;
        }
        
        console.log('Profile data:', profileData);
        
        if (!profileData || !profileData.org_id) {
          console.error('No organization ID found in user profile');
          createToast('User profile has no organization ID', 'error');
          loading.value = false;
          return;
        }
        
        const organizationId = profileData.org_id;
        console.log('Organization ID:', organizationId);
        
        // Fetch organization details to get currency
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('currency')
          .eq('id', organizationId)
          .single();
          
        if (!orgError && orgData && orgData.currency && typeof orgData.currency === 'string') {
          const currencyValue = orgData.currency.trim();
          console.log('Organization currency value:', currencyValue);
          
          // Check if it's a standard 3-letter currency code
          const isStandardCode = /^[A-Z]{3}$/i.test(currencyValue);
          
          if (isStandardCode) {
            // It's a standard code like USD, EUR, ILS
            organizationCurrency.value = currencyValue.toUpperCase();
            currencySymbol.value = getCurrencySymbol(organizationCurrency.value);
            console.log('Using standard currency code:', organizationCurrency.value, 'with symbol:', currencySymbol.value);
          } else {
            // It's probably a symbol like $, €, ₪, etc.
            currencySymbol.value = currencyValue;
            
            // Try to map the symbol to a currency code
            const knownSymbols: Record<string, string> = {
              '$': 'USD',
              '€': 'EUR',
              '£': 'GBP',
              '¥': 'JPY',
              '₽': 'RUB',
              '₹': 'INR',
              '₪': 'ILS',
            };
            
            organizationCurrency.value = knownSymbols[currencyValue] || 'USD';
            console.log('Using currency symbol:', currencySymbol.value, 'with code:', organizationCurrency.value);
          }
        } else {
          console.warn('Could not fetch organization currency, using default:', organizationCurrency.value);
          if (orgError) console.error('Organization fetch error:', orgError);
        }
        
        // Let's try a debug query to see all deals regardless of organization
        const { data: debugAllDeals, error: debugError } = await supabase
          .from('deals')
          .select('id, title, organization_id')
          .limit(10);
          
        console.log('DEBUG - All deals in system:', debugAllDeals, debugError);
        
        // Try a direct query with raw SQL to bypass any potential ORM issues
        const { data: rawSqlDeals, error: rawSqlError } = await supabase
          .rpc('get_deals_by_org', { org_id: organizationId });
          
        console.log('DEBUG - Raw SQL query result:', rawSqlDeals, rawSqlError);
        
        // Let's try a direct query first to see if any deals exist for this organization
        const { data: dealsCheck, error: dealsCheckError } = await supabase
          .from('deals')
          .select('id, title, organization_id')
          .eq('organization_id', organizationId)
          .limit(5);
          
        if (dealsCheckError) {
          console.error('Error checking deals existence:', dealsCheckError);
        } else {
          console.log('Deals check (first 5):', dealsCheck);
          console.log('Deals check SQL query:', (dealsCheck as any)?._query?.[0]?.query);
        }
        
        // Load deals with related client details
        console.log('Fetching deals with client details...');
        // Using basic query without JOIN to avoid TS errors 
        const { data, error } = await supabase
          .from('deals')
          .select('*')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false });
          
        if (error) {
          console.error('Error fetching deals with client details:', error);
          throw error;
        }
        
        console.log('Deals data from API:', data);
        
        // Convert data to the structure we need
        if (data && data.length > 0) {
          console.log('Processing', data.length, 'deals');
          
          // If we need client data, we'll fetch it separately
          const clientIds = data
            .filter(deal => deal.connecttoclients)
            .map(deal => deal.connecttoclients);
            
          let clientsMap: Record<string, { first_name?: string, last_name?: string }> = {};
          
          if (clientIds.length > 0) {
            console.log('Fetching client data for', clientIds.length, 'clients');
            const { data: clientsData, error: clientsError } = await supabase
              .from('clients')
              .select('id, first_name, last_name')
              .in('id', clientIds);
              
            if (!clientsError && clientsData) {
              clientsMap = (clientsData as Client[]).reduce((acc, client) => {
                const clientId = client.id;
                acc[clientId] = {
                  first_name: client.first_name,
                  last_name: client.last_name
                };
                return acc;
              }, {} as Record<string, { first_name?: string, last_name?: string }>);
            }
          }
          
          deals.value = data.map((deal: any) => {
            let first_name = null;
            let last_name = null;
            
            // If this deal has a client, get the client data from our map
            if (deal.connecttoclients && clientsMap[deal.connecttoclients]) {
              first_name = clientsMap[deal.connecttoclients].first_name || null;
              last_name = clientsMap[deal.connecttoclients].last_name || null;
            }
            
            return {
              ...deal,
              first_name,
              last_name
            };
          }) as Deal[];
          console.log('Processed deals:', deals.value);
        } else {
          console.log('No deals found or data is empty');
          deals.value = [];
        }
        
      } catch (error) {
        console.error('Error loading deals:', error);
        createToast('Error loading deals', 'error');
      } finally {
        loading.value = false;
      }
    };
    
    // Functions for deal actions
    const viewDeal = (id: string) => {
      console.log('View deal:', id);
      router.push(`/crm/deals/${id}`);
    };
    
    const editDeal = (id: string) => {
      console.log('Edit deal:', id);
      router.push(`/crm/deals/${id}`);
    };
    
    const confirmDeleteDeal = (id: string) => {
      dealToDelete.value = id;
      showDeletePopup.value = true;
    };
    
    const cancelDelete = () => {
      showDeletePopup.value = false;
      dealToDelete.value = null;
    };
    
    const confirmDelete = () => {
      if (dealToDelete.value) {
        deleteDeal(dealToDelete.value);
        showDeletePopup.value = false;
        dealToDelete.value = null;
      }
    };
    
    const deleteDeal = async (id: string) => {
      try {
        const { error } = await supabase
          .from('deals')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        // Remove the deal from the list
        deals.value = deals.value.filter(deal => deal.id !== id);
        createToast('Deal deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting deal:', error);
        createToast('Error deleting deal', 'error');
      }
    };
    

    
    // Helper to get currency symbol from code
    const getCurrencySymbol = (currencyCode: string): string => {
      // Create a formatter for the currency
      try {
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: 0,
        });
        
        // Extract just the symbol
        const parts = formatter.formatToParts(0);
        const symbolPart = parts.find(part => part.type === 'currency');
        return symbolPart ? symbolPart.value : currencyCode;
      } catch (error) {
        console.warn('Error getting currency symbol:', error);
        return currencyCode;
      }
    };
    
    onMounted(() => {
      fetchDeals();
      // Listen for refresh events
      emitter.on('refresh-deals', fetchDeals);
    });

    onBeforeUnmount(() => {
      // Clean up event listener
      emitter.off('refresh-deals', fetchDeals);
    });

    return {
      deals,
      loading,
      searchTerm,
      filteredDeals,
      formatCurrency,
      formatDate,
      shortId,
      getStatusText,
      getStatusClass,
      viewDeal,
      editDeal,
      confirmDeleteDeal,
      showDeletePopup,
      dealToDelete,
      cancelDelete,
      confirmDelete,
      directT
    };
  }
});
</script>

<style scoped>
/* Modern styles handled by Tailwind classes */
</style>
